import React from 'react';
import PropTypes from 'prop-types';
import { CORRELATION_CARD } from 'src/sections/GroupCards/cardTypes';
import { TREE_MAP, SIMPLE_BUBBLE, GRAPH_BUBBLE } from 'src/components/Charts/types';
import SimpleBubbleChart from './echarts/SimpleBubbleChart';
import GraphBubbleChart from './echarts/GraphBubbleChart';
import TreeMapChart from './echarts/TreeMapChart';

/**
 * Resolve o componente de gráfico e o tipo associado à sua configuração.
 * Os três tipos da correlação compartilham o mesmo pipeline do ChartManager
 * (color picker, fontSize, esquema de cores), por isso recebem o mesmo conjunto de props.
 *
 * @param {string} type type of the chart that will be rendered.
 * where type = simple_bubble|graph_bubble|tree_map
 */
function resolveChart(type = 'simple_bubble') {
  return {
    [SIMPLE_BUBBLE]: { Component: SimpleBubbleChart, chartType: SIMPLE_BUBBLE },
    [GRAPH_BUBBLE]: { Component: GraphBubbleChart, chartType: GRAPH_BUBBLE },
    [TREE_MAP]: { Component: TreeMapChart, chartType: TREE_MAP },
  }[type];
}

const propTypes = {
  type: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  legendsFilter: PropTypes.string,
  legendsFilterHandler: PropTypes.func,
  isFullscreen: PropTypes.bool,
  theme: PropTypes.shape({
    legend: PropTypes.object,
    colors: PropTypes.array,
  }).isRequired,
  updateChartColors: PropTypes.func,
  colorPickerColors: PropTypes.array,
  colors: PropTypes.array,
  chartFontSize: PropTypes.number,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.object]),
  chartRef: PropTypes.object,
};

const defaultProps = {
  type: 'simple_bubble',
  data: [],
  legendsFilter: '',
  isFullscreen: false,
  legendsFilterHandler: null,
  updateChartColors: undefined,
  colorPickerColors: [],
  colors: [],
  chartFontSize: 14,
  error: null,
  chartRef: undefined,
};

export default function CorrelationChart({
  type,
  data,
  legendsFilter,
  legendsFilterHandler,
  isFullscreen,
  theme,
  updateChartColors,
  colorPickerColors,
  colors,
  chartFontSize,
  error,
  chartRef,
}) {
  const resolved = resolveChart(type);
  if (!resolved) return null;
  const { Component, chartType } = resolved;

  // Mantém a sentinela legada para os charts que ainda usam `data.isEmpty()` internamente.
  data.isEmpty = () => !data.alternativas
      || data.alternativas.length === 0;

  return (
    <Component
      // A `key` força a remontagem do gráfico quando o tema da aplicação ou o estado
      // de erro de configuração muda, evitando estados intermediários inconsistentes.
      key={`${theme.title}${error}${type}`}
      data={data}
      legendsFilter={legendsFilter}
      legendsFilterHandler={legendsFilterHandler}
      isFullscreen={isFullscreen}
      theme={theme}
      updateChartColors={updateChartColors}
      colorPickerColors={colorPickerColors}
      colors={colors}
      chartFontSize={chartFontSize}
      error={error}
      chartRef={chartRef}
      cardType={CORRELATION_CARD}
      chartType={chartType}
    />
  );
}

CorrelationChart.propTypes = propTypes;
CorrelationChart.defaultProps = defaultProps;
